import Vue from 'vue';
import vcitaLogo from '@/assets/images/vcita-logo-white.svg';
import LayoutConstants from '@/constansts/layoutConstants';
import businessService from '@/modules/business/services/businessService';
import businessDateFormats from '@/modules/common/filters/BusinessDateFormats';
import businessContactDetailsStore from '@/modules/business/store/businessContactDetailsStore';
import businessSmsStore from '@/modules/business/store/businessSmsStore';
import eventListenerService from '@/utils/eventCommunicationUtils/eventListenerService';
import reviewSettingsService from '@/modules/reviews/services/reviewSettingsService';
import logError from '@/utils/logError/logError';

const state = {
  matterData: {
    matterTerm: 'clients',
    icon: 'icon-clients-POV',
    translation: 'common.navigation.clients',
  },
  branding: {
    colors: {
      primary: '#FFFFFF', // white so that the menu doesn't have a background color before brand is loaded
      secondary: '#0093B8',
      text: '#ffffff',
    },
    logo: null,
    poweredBy: {
      parent_logo: '',
      intandem_logo: '',
    },
  },
  isBrandingLoaded: false,
  features: [],
  featureFlagsLoaded: false,
  attributes: {},
  businessProfile: null,
  reviewSettings: null,
  analyticsConfig: {
    disableAnalytics: null,
  },
  /** Business logo URL from v2/settings/businesses (logo_path). Used by Web AI Widget default / reset. */
  businessLogoUrl: null,
  /** Business text color from v2/settings/businesses (branding.text_color). Used by Web AI Widget default / reset. */
  businessSettingsTextColor: null,
};

const helpers = {
  /**
   * Get forced feature flags from URL and localStorage (non-production only)
   *
   * URL: ?forceFeatureFlagName=FEATURE_NAME (same as Angular, single flag)
   * localStorage: addFeatureFlags = ["flag1", "flag2"] (array of flags, persists across reloads)
   *
   * @returns {string[]} Array of forced feature flag names
   */
  getForcedFeatureFlags: () => {
    const isProduction = window.pov_config?.VUE_APP_ENV === 'production';
    if (isProduction) return [];

    const forcedFlags = [];

    // Check URL param (single flag, same as Angular)
    const urlParams = new URLSearchParams(window.location.search);
    const urlFlag = urlParams.get('forceFeatureFlagName');
    if (urlFlag) {
      forcedFlags.push(urlFlag);
    }

    // Check localStorage (array of flags, persists across reloads)
    try {
      const storedFlags = localStorage.getItem('addFeatureFlags');
      if (storedFlags) {
        const parsed = JSON.parse(storedFlags);
        if (Array.isArray(parsed)) {
          forcedFlags.push(...parsed);
        }
      }
    } catch (e) {
      console.warn('[DEV] Failed to parse addFeatureFlags from localStorage:', e);
    }

    return [...new Set(forcedFlags)]; // Remove duplicates
  },

  /**
   * Normalize branding colors from either API shape:
   * - Nested: branding.colors = { primary, secondary, text [, brand_color] }
   * - Flat (profile.brand / hostBrand): branding.primary_color, branding.secondary_color, branding.text_color, branding.brand_color
   */
  normalizeBrandingColors(branding) {
    if (branding.colors && typeof branding.colors === 'object') {
      const colors = { ...branding.colors };
      if (branding.brand_color != null) colors.brand_color = branding.brand_color;
      else if (branding.colors.brand_color != null) colors.brand_color = branding.colors.brand_color;
      return colors;
    }
    if (branding.primary_color != null || branding.secondary_color != null) {
      return {
        primary: branding.primary_color ?? branding.colors?.primary,
        secondary: branding.secondary_color ?? branding.colors?.secondary,
        text: branding.text_color ?? branding.colors?.text,
        brand_color: branding.brand_color ?? branding.colors?.brand_color,
      };
    }
    return {};
  },
  mapBrandingResponse: (branding, rootGetters) => {
    const colors = helpers.normalizeBrandingColors(branding);
    const primaryForLogo = colors.primary || colors.secondary;

    const poweredByMapped = {};
    const isMobile = rootGetters['CommonStore/isMobile'];

    if (isMobile || helpers.isPrimaryColorWhite(primaryForLogo)) {
      poweredByMapped.parent_logo = branding.powered_by?.parent_logo_dark || '';
      poweredByMapped.intandem_logo = branding.powered_by?.intandem_logo_dark || '';
    } else {
      poweredByMapped.parent_logo = branding.powered_by?.parent_logo_bright || '';
      poweredByMapped.intandem_logo = branding.powered_by?.intandem_logo_bright || '';
    }
    const brandingObject = { colors, logo: branding.logo || vcitaLogo, poweredBy: poweredByMapped };
    return brandingObject;
  },
  isPrimaryColorWhite: (primaryColor) => ['#ffffff', '#fff', '#ffffffff', 'rgb(255, 255, 255)'].includes(primaryColor.toLowerCase()),
  supportArticleUrl: (articleCode, attributes, brandedZendeskEnabled) => {
    if (brandedZendeskEnabled && attributes?.directory_settings?.faq_url) {
      const url = new URL(attributes.directory_settings.faq_url);
      url.searchParams.append('article_code', articleCode);

      return url.toString();
    }
    return '';
  },
  brandedZendeskSupportArticleUrl: (articleCode, attributes, rootGetters) => {
    const brandedZendeskEnabled = rootGetters['BusinessStore/hasFeature']('multi_branded_zendesk');
    return helpers.supportArticleUrl(articleCode, attributes, brandedZendeskEnabled);
  },
  brandedSupportArticleUrl: (articleCode, attributes, rootGetters, {
    partnerDirectoryUrl = '',
    directUrl = '',
  } = {}) => {
    const zendeskUrl = helpers.brandedZendeskSupportArticleUrl(articleCode, attributes, rootGetters);
    if (zendeskUrl) {
      return zendeskUrl;
    }
    if (partnerDirectoryUrl && rootGetters['BusinessStore/isPartnerDirectory']) {
      return partnerDirectoryUrl;
    }
    if (directUrl) {
      return directUrl;
    }
    return null;
  },
  buildMatterData: (matterTerm) => {
    const translationKey = LayoutConstants.MATTER_TRANSLATIONS_KEYS[matterTerm];
    const matterData = {
      matterTerm,
      icon: `icon-${matterTerm.toLowerCase()}-POV`,
      translation: translationKey,
    };
    return matterData;
  },
  /** Resolve logo_path from v2/settings/businesses to full URL (prepend Cloudinary base if path is relative). */
  resolveBusinessLogoUrl(logoPath) {
    if (!logoPath) return null;
    const base = typeof window !== 'undefined' && window.pov_config?.VUE_APP_CLOUDINARY;
    return (base && !/^https?:\/\//i.test(logoPath))
      ? `${base.replace(/\/$/, '')}/${logoPath.replace(/^\//, '')}`
      : logoPath;
  },
  /**
   * Account brand color from GET v2/settings/businesses `branding` (SettingsBusinessDecorator#calc_branding).
   * theme_top_color may be blank while primary/secondary are set.
   */
  resolveSettingsBrandingBrandColor(branding) {
    if (!branding || typeof branding !== 'object') return null;
    const keys = ['theme_top_color', 'primary_color', 'secondary_color', 'bg_top_color'];
    for (let i = 0; i < keys.length; i += 1) {
      const v = branding[keys[i]];
      if (v != null && String(v).trim() !== '') {
        return String(v).trim();
      }
    }
    return null;
  },
  buildAvailableLocales: (currentLocale, hasCustomLocalesFeature, isFree) => {
    const baseLocales = [
      { key: 'en', title: 'English (US)' },
      { key: 'es', title: 'Español' },
      { key: 'fr', title: 'Français' },
      { key: 'it', title: 'Italiano' },
      { key: 'pt', title: 'Português' },
      { key: 'de', title: 'Deutsch' },
      { key: 'pl', title: 'Polski' },
      { key: 'nl', title: 'Nederlands' },
      { key: 'en-GB', title: 'English (UK)' },
    ];

    const conditionalLocales = [];

    if (currentLocale === 'he' || !isFree) {
      conditionalLocales.push({ key: 'he', title: 'עברית' });
    }

    if (currentLocale === 'sl') {
      conditionalLocales.push({ key: 'sl', title: 'Slovenščina' });
    }

    if (hasCustomLocalesFeature) {
      conditionalLocales.push({ key: 'keys', title: 'Exposed keys' });
      conditionalLocales.push({ key: 'pseudo', title: 'Pseudo translations' });
    }

    return [...baseLocales, ...conditionalLocales];
  },
};

const mutations = {
  setMatterData(state, matterData) {
    state.matterData = matterData;
  },
  setBranding(state, branding) {
    const existingBrandColor = state.branding?.colors?.brand_color;
    state.branding = branding;
    state.isBrandingLoaded = true;
    // Always reapply business theme color (v2/settings/businesses) when we had it: main branding API may return a different brand_color (e.g. primary #071B33) which would otherwise overwrite it
    if (existingBrandColor && state.branding?.colors) {
      state.branding.colors.brand_color = existingBrandColor;
    }
  },
  /** Use business brand color from parent (Angular profile.brand), same source as Vue's hostBrand */
  setBrandColorFromHost(state, brandColor) {
    if (!state.branding?.colors) {
      console.warn('[setBrandColorFromHost] state.branding.colors missing, cannot set brand_color:', brandColor);
      return;
    }
    if (brandColor) {
      state.branding.colors.brand_color = brandColor;
    }
  },
  /** Business logo URL from v2/settings/businesses (logo_path). */
  setBusinessLogoUrl(state, url) {
    state.businessLogoUrl = url || null;
  },
  /** Business text color from v2/settings/businesses (branding.text_color). */
  setBusinessSettingsTextColor(state, color) {
    state.businessSettingsTextColor = color || null;
  },
  setAttribute(state, payload) {
    state.attributes = { ...state.attributes, [payload.key]: payload.value };
  },
  setAttributes(state, attributes) {
    state.attributes = attributes;
  },
  setFeatureFlags(state, features) {
    state.features = features;
  },
  setFeatureFlagsLoaded(state, loaded) {
    state.featureFlagsLoaded = loaded;
  },
  setReviewSettings(state, settings) {
    state.reviewSettings = settings;
  },
  setBusinessProfile(state, profile) {
    state.businessProfile = profile;
  },
  setAnalyticsConfig(state, config) {
    state.analyticsConfig = { ...state.analyticsConfig, ...config };
  },
};

const actions = {
  async fetchBranding({ commit, rootGetters }) {
    try {
      const branding = await businessService.fetchBranding();
      if (branding) {
        const mappedBranding = helpers.mapBrandingResponse(branding, rootGetters);
        commit('setBranding', mappedBranding);
      }
      // Business brand color and logo from same source as business settings page: GET v2/settings/businesses
      const businessUid = rootGetters['AuthStore/businessUid'];
      if (businessUid) {
        try {
          const settings = await businessService.fetchBusinessSettings(businessUid);
          const brandColor = helpers.resolveSettingsBrandingBrandColor(settings?.branding);
          if (brandColor) {
            commit('setBrandColorFromHost', brandColor);
          }
          const logoUrl = helpers.resolveBusinessLogoUrl(settings?.logo_path);
          commit('setBusinessLogoUrl', logoUrl || null);
          const textColor = settings?.branding?.text_color;
          if (textColor) {
            commit('setBusinessSettingsTextColor', textColor);
          }
        } catch (_) {
          // ignore
        }
      }
    } catch (e) {
      console.error('error', e);
    }
  },

  /**
   * Load business brand color and logo from v2/settings/businesses (same source as business settings page).
   * Call when businessUid is available (e.g. Web AI Widget page).
   */
  async fetchBusinessBrandColor({ commit, rootGetters, state }) {
    const businessUid = rootGetters['AuthStore/businessUid'];
    if (!businessUid) {
      console.warn('[fetchBusinessBrandColor] No businessUid, skipping');
      return;
    }
    try {
      const settings = await businessService.fetchBusinessSettings(businessUid);
      const brandColor = helpers.resolveSettingsBrandingBrandColor(settings?.branding);
      const hasColors = !!state.branding?.colors;
      if (brandColor) {
        commit('setBrandColorFromHost', brandColor);
        console.log('[fetchBusinessBrandColor] Set brand_color from settings branding:', brandColor, 'had state.branding.colors:', hasColors);
      } else {
        console.warn('[fetchBusinessBrandColor] No brand color in settings branding (theme_top / primary / secondary / bg_top). branding present:', !!settings?.branding);
      }
      const logoUrl = helpers.resolveBusinessLogoUrl(settings?.logo_path);
      commit('setBusinessLogoUrl', logoUrl || null);
      const textColor = settings?.branding?.text_color;
      if (textColor) {
        commit('setBusinessSettingsTextColor', textColor);
      }
    } catch (e) {
      // Widget editor uses businessSettingsBrandColor for "Reset to business color"; when this fails, that button stays hidden
      console.warn('[fetchBusinessBrandColor] failed (v2/settings/businesses):', e?.message || e);
    }
  },

  async fetchAttributes({ commit, rootGetters }) {
    try {
      const attributes = await businessService.fetchAttributes();
      commit('setAttributes', attributes);
      // eslint-disable-next-line camelcase
      const { locale, time_format } = attributes;
      Vue.prototype.$ds.setLocale(locale || 'en');
      Vue.prototype.setLocale(locale || 'en');
      Vue.prototype.addI18nModule('common-pov');
      businessDateFormats.setLocale(locale, time_format);

      window.supportArticleUrl = (
        articleCode,
        {
          partnerDirectoryUrl = '',
          directUrl = '',
        } = {},
      ) => helpers.brandedSupportArticleUrl(articleCode, attributes, rootGetters, {
        partnerDirectoryUrl,
        directUrl,
      });

      window.openSupportArticle = (articleCode, {
        partnerDirectoryUrl = '',
        directUrl = '',
      } = {}) => {
        const url = window.supportArticleUrl(articleCode, {
          partnerDirectoryUrl,
          directUrl,
        });
        if (url) {
          window.open(`${url.toString()}`, '_blank');
        }
      };

      const matterTerm = attributes.matter_term;
      if (matterTerm) {
        commit('setMatterData', helpers.buildMatterData(matterTerm));
      }
    } catch (e) {
      console.error('error', e);
    }
  },
  setAttribute({ commit }, payload) {
    commit('setAttribute', payload);
    if (payload.key === 'time_format') {
      businessDateFormats.setLocale(null, payload.value);
    }
  },

  async fetchFeatureFlags({ rootGetters, commit }) {
    try {
      const businessUid = rootGetters['AuthStore/businessUid'];
      let features = await businessService.fetchFeaturesFlags(businessUid) || [];

      // Add forced feature flags from URL and localStorage (non-production only)
      const forcedFlags = helpers.getForcedFeatureFlags();
      if (forcedFlags.length > 0) {
        const newFlags = forcedFlags.filter((flag) => !features.includes(flag));
        if (newFlags.length > 0) {
          features = [...features, ...newFlags];
          console.info(`[DEV] Force enabled feature flags: ${newFlags.join(', ')}`);
        }
      }

      const loaded = features?.length > 0;
      commit('setFeatureFlagsLoaded', loaded);
      commit('setFeatureFlags', features);
      if (loaded) {
        eventListenerService.emit('featureFlagsLoaded');
      }
    } catch (e) {
      console.error('error', e);
    }
  },
  async getReviewSettings({ rootGetters, commit }) {
    const res = {};

    try {
      const businessUid = rootGetters['AuthStore/businessUid'];
      const settings = await reviewSettingsService.getReviewSettings(businessUid);
      commit('setReviewSettings', settings);
    } catch (e) {
      console.error('error', e);
      res.error = e;
    }

    return res;
  },
  setAnalyticsConfig({ commit }, config) {
    commit('setAnalyticsConfig', config);
  },
  async setReviewSettings({ rootGetters, state, commit }, settings) {
    const res = {};

    try {
      const businessUid = rootGetters['AuthStore/businessUid'];
      let newSettings;

      if (state.reviewSettings) {
        newSettings = await reviewSettingsService.updateReviewSettings(businessUid, settings);
      } else {
        newSettings = await reviewSettingsService.createReviewSettings(settings);
      }

      commit('setReviewSettings', newSettings);
    } catch (e) {
      console.error('error', e);
      res.error = e;
    }

    return res;
  },
  async fetchBusinessProfile({ state, commit }, { force = false } = {}) {
    if (state.businessProfile && !force) return;
    try {
      const profile = await businessService.fetchBusinessProfile();
      commit('setBusinessProfile', profile);
    } catch (e) {
      logError('Error fetching business profile', e);
    }
  },
};

const getters = {
  matterTerm: (state) => state.matterData.matterTerm,
  matterTermIcon: (state) => state.matterData.icon,
  matterTermTranslation: (state) => state.matterData.translation,
  primaryColor: (state) => state.branding.colors.primary,
  /** Business settings brand color (e.g. #4DA51E); falls back to primary for menu/chrome. */
  brandColor: (state) => state.branding.colors.brand_color || state.branding.colors.primary,
  /** Brand color from v2/settings/businesses branding (see resolveSettingsBrandingBrandColor); stored in colors.brand_color after fetchBusinessBrandColor. */
  businessSettingsBrandColor: (state) => state.branding?.colors?.brand_color ?? null,
  /** Business logo URL from v2/settings/businesses (logo_path). For widget default / reset. */
  businessLogoUrl: (state) => state.businessLogoUrl,
  /** Business text color from v2/settings/businesses (branding.text_color). For widget default / reset. */
  businessSettingsTextColor: (state) => state.businessSettingsTextColor ?? null,
  secondaryColor: (state) => state.branding.colors.secondary,
  textColor: (state) => state.branding.colors.text,
  isBrandingLoaded: (state) => state.isBrandingLoaded,
  logo: (state) => state.branding.logo,
  parentPoweredByLogo: (state) => state.branding.poweredBy.parent_logo,
  inTandemPoweredByLogo: () => state.branding.poweredBy.intandem_logo,
  attributes: (state) => state.attributes,
  brandName: (state) => state.attributes?.brand_name,
  featureFlags: (state) => state.features,
  featureFlagsLoaded: (state) => state.featureFlagsLoaded,
  hasFeature: (state) => (fName) => state.features.includes(fName),
  businessProfile: (state) => state.businessProfile,
  livesiteUrl: (state) => state.businessProfile?.livesite_url,
  appHost: (state) => state.attributes.app_host,
  directorySettings: (state) => state.attributes?.directory_settings || {},
  isPartnerDirectory: (state) => (
    !!state.attributes?.directory_settings?.is_whitelabel || !!state.attributes?.directory_settings?.is_co_branded_managed
  ),
  getDirectoryLogoutUrl: (state) => (
    state.attributes?.directory_settings?.logout_url
  ),
  hasHelpLinks: (state) => !!state.attributes?.directory_settings?.custom_support_url
    || !!state.attributes?.directory_settings?.faq_url
    || !!state.attributes?.directory_settings?.help_links,
  supportArticleUrl: (state) => (articleCode) => helpers.supportArticleUrl(articleCode, state.attributes, getters.hasFeature(state)('multi_branded_zendesk')),
  isSpecialDirectory: (state) => (
    state.attributes?.directory_settings?.is_whitelabel || state.attributes?.directory_settings?.is_co_branded_managed || false
  ),
  // TODO: test the isBranded getter, is it the same as before?
  // isBranded: state => state.branding && (state.branding.whitelabel || state.branding.co_branded_managed), <- frontge-vue
  isBranded: (state) => !!(state.attributes?.directory_settings?.is_whitelabel
    || state.attributes?.directory_settings?.is_co_branded_managed
    || state.attributes?.directory_settings?.is_managed_partner),
  locale: (state) => state.attributes.locale || 'en',
  reviewSettings: (state) => state.reviewSettings,
  analyticsConfig: (state) => state.analyticsConfig,
  isAnalyticsConfigReady: (state) => state.analyticsConfig.disableAnalytics !== null,
  isAnalyticsDisabled: (state) => state.analyticsConfig.disableAnalytics !== false,
  availableLocales: (state) => helpers.buildAvailableLocales(
    getters.locale(state),
    getters.hasFeature(state)('custom_locales'),
    state.attributes?.is_free,
  ),
};

const modules = {
  BusinessContactDetailsStore: businessContactDetailsStore,
  businessSms: businessSmsStore,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules,
};
